import React, { useEffect, useState } from 'react';
import { BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { TrendingUp, Clock, Award, Target, Calendar, Activity } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { useSkillsTracker } from '../contexts/SkillsTrackerContext';

interface AnalyticsData {
  totalHoursInvested: number;
  averageProgress: number;
  skillsCompleted: number;
  activeSkills: number;
  recentActivity: Array<{
    date: string;
    hours: number;
    activities: number;
  }>;
  progressByCategory: Array<{
    category: string;
    progress: number;
    count: number;
  }>;
  proficiencyDistribution: Array<{
    level: string;
    count: number;
  }>;
  timeAllocation: Array<{
    name: string;
    hours: number;
  }>;
}

export const SkillsAnalyticsDashboard: React.FC = () => {
  const { skills } = useSkillsTracker();
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  useEffect(() => {
    fetchAnalytics();
  }, [skills, timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Calculate date range
      const now = new Date();
      let startDate = new Date();
      if (timeRange === '7d') startDate.setDate(now.getDate() - 7);
      else if (timeRange === '30d') startDate.setDate(now.getDate() - 30);
      else if (timeRange === '90d') startDate.setDate(now.getDate() - 90);
      else startDate = new Date(0);

      // Fetch progress updates
      const { data: progressUpdates } = await supabase
        .from('skill_progress_updates')
        .select('*')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      // Calculate total hours
      const totalHours = progressUpdates?.reduce((sum, update) => sum + (update.hours_invested || 0), 0) || 0;

      // Calculate progress by category
      const categoryProgress = new Map<string, { total: number; count: number }>();
      skills.forEach(skill => {
        const cat = skill.skill.category;
        if (!categoryProgress.has(cat)) {
          categoryProgress.set(cat, { total: 0, count: 0 });
        }
        const data = categoryProgress.get(cat)!;
        data.total += skill.progress_percentage;
        data.count += 1;
      });

      const progressByCategory = Array.from(categoryProgress.entries()).map(([category, data]) => ({
        category,
        progress: Math.round(data.total / data.count),
        count: data.count
      }));

      // Calculate proficiency distribution
      const proficiencyDist = new Map<string, number>();
      skills.forEach(skill => {
        const level = skill.current_proficiency_level;
        proficiencyDist.set(level, (proficiencyDist.get(level) || 0) + 1);
      });

      const proficiencyDistribution = Array.from(proficiencyDist.entries()).map(([level, count]) => ({
        level: level.charAt(0).toUpperCase() + level.slice(1),
        count
      }));

      // Calculate recent activity (last 7 days)
      const recentActivity: Array<{ date: string; hours: number; activities: number }> = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];

        const dayUpdates = progressUpdates?.filter(u =>
          u.created_at.startsWith(dateStr)
        ) || [];

        recentActivity.push({
          date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          hours: dayUpdates.reduce((sum, u) => sum + (u.hours_invested || 0), 0),
          activities: dayUpdates.length
        });
      }

      // Calculate time allocation by skill
      const skillHours = new Map<string, number>();
      progressUpdates?.forEach(update => {
        const skill = skills.find(s => s.id === update.user_skill_id);
        if (skill) {
          const name = skill.skill.skill_name;
          skillHours.set(name, (skillHours.get(name) || 0) + (update.hours_invested || 0));
        }
      });

      const timeAllocation = Array.from(skillHours.entries())
        .map(([name, hours]) => ({ name, hours: parseFloat(hours.toFixed(1)) }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 5);

      setAnalytics({
        totalHoursInvested: parseFloat(totalHours.toFixed(1)),
        averageProgress: Math.round(skills.reduce((sum, s) => sum + s.progress_percentage, 0) / (skills.length || 1)),
        skillsCompleted: skills.filter(s => s.status === 'completed').length,
        activeSkills: skills.filter(s => s.status === 'active').length,
        recentActivity,
        progressByCategory,
        proficiencyDistribution,
        timeAllocation
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center p-12">
        <p className="text-gray-600">No analytics data available</p>
      </div>
    );
  }

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Skills Analytics</h2>
        <div className="flex gap-2">
          {(['7d', '30d', '90d', 'all'] as const).map(range => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                timeRange === range
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {range === 'all' ? 'All Time' : range.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Total Hours</span>
            <Clock className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{analytics.totalHoursInvested}</p>
          <p className="text-sm text-gray-500 mt-1">Time invested</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Avg Progress</span>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{analytics.averageProgress}%</p>
          <p className="text-sm text-gray-500 mt-1">Across all skills</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Completed</span>
            <Award className="w-5 h-5 text-yellow-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{analytics.skillsCompleted}</p>
          <p className="text-sm text-gray-500 mt-1">Skills mastered</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-gray-600 text-sm">Active Skills</span>
            <Target className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-3xl font-bold text-gray-900">{analytics.activeSkills}</p>
          <p className="text-sm text-gray-500 mt-1">In progress</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Recent Activity (Last 7 Days)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={analytics.recentActivity}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="hours" fill="#3B82F6" name="Hours" />
              <Bar dataKey="activities" fill="#10B981" name="Activities" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Progress by Category */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Target className="w-5 h-5" />
            Progress by Category
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={analytics.progressByCategory} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis dataKey="category" type="category" width={100} />
              <Tooltip />
              <Bar dataKey="progress" fill="#8B5CF6" name="Avg Progress %" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Time Allocation */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Time Allocation (Top 5 Skills)
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={analytics.timeAllocation}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="hours"
              >
                {analytics.timeAllocation.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Proficiency Distribution */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Proficiency Distribution
          </h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={analytics.proficiencyDistribution}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="level" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#F59E0B" name="Number of Skills" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights</h3>
        <div className="space-y-2">
          {analytics.totalHoursInvested > 0 && (
            <p className="text-sm text-gray-700">
              📊 You've invested <strong>{analytics.totalHoursInvested} hours</strong> in skill development in the selected period.
            </p>
          )}
          {analytics.averageProgress >= 50 && (
            <p className="text-sm text-gray-700">
              🎯 Great progress! You're averaging <strong>{analytics.averageProgress}%</strong> completion across your skills.
            </p>
          )}
          {analytics.skillsCompleted > 0 && (
            <p className="text-sm text-gray-700">
              🏆 You've completed <strong>{analytics.skillsCompleted} skill{analytics.skillsCompleted > 1 ? 's' : ''}</strong>. Keep up the momentum!
            </p>
          )}
          {analytics.activeSkills > 5 && (
            <p className="text-sm text-gray-700">
              ⚠️ You're tracking {analytics.activeSkills} active skills. Consider focusing on fewer skills for faster progress.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
